import { FC, PropsWithChildren, useMemo } from 'react';

import { AccessLevel } from '@/entities/vfo/types';

import { useIsFounder } from '../../hooks/useIsFounder';
import { usePermissions } from '../../hooks/usePermissions';

type PermissionGuardProps = PropsWithChildren & {
  permission?: string | string[];
  role?: AccessLevel | AccessLevel[];
  requiredAll?: boolean;
};

export const PermissionGuard: FC<PermissionGuardProps> = ({
  permission,
  role,
  children,
  requiredAll,
}) => {
  const { hasAnyPermission, hasAllPermissions, hasAnyRole, hasAllRoles } = usePermissions();

  const isFounder = useIsFounder();

  const hasAccess = useMemo(() => {
    if (isFounder) return true;

    if (permission) {
      const permissionArray = Array.isArray(permission) ? permission : [permission];

      if (requiredAll) {
        return hasAllPermissions(permissionArray);
      }
      return hasAnyPermission(permissionArray);
    }

    if (role) {
      const roleArray = Array.isArray(role) ? role : [role];

      if (requiredAll) {
        return hasAllRoles(roleArray);
      }
      return hasAnyRole(roleArray);
    }

    return true;
  }, [permission, role, isFounder, hasAllPermissions, hasAllRoles]);

  if (!hasAccess) {
    return null;
  }

  return children;
};
